/**
 * Clubmonee Gravur (FEATURE-0002) — Mengen-Kopplung Ring ↔ Gravurartikel im Warenkorb.
 *
 * Ring und gepaarter Gravurartikel teilen dieselbe versteckte `_Gravur ID`; die
 * Gravurartikel-Zeile trägt zusätzlich den Marker `_Gravur Artikel` und hat KEINEN
 * eigenen Mengen-Auswähler. Ändert der Kunde die Menge des RINGS, muss die gepaarte
 * Gravurartikel-Zeile automatisch dieselbe Menge übernehmen (sonst: 3 Ringe, aber nur
 * 1 Gravur berechnet/gefertigt).
 *
 * Mechanik: Das Theme (assets/theme.js, `line-item`-Element) verarbeitet Mengenänderungen
 * über einen Bubble-Phase-Delegate auf `change`-Events von `[data-line-key]` sowie Klicks
 * auf `[href*="cart/change"]` und ruft `/cart/change.js` für EINE Zeile. Dieses Modul hängt
 * sich in der CAPTURE-Phase auf `document` ein (läuft VOR dem Theme-Delegate), erkennt
 * Mengenänderungen an einer gepaarten RING-Zeile und übernimmt sie: EIN atomarer
 * `/cart/update.js`-Request setzt Ring UND Gravurartikel auf dieselbe Menge, danach werden
 * exakt die Events gefeuert, die das Theme selbst nutzt (`cart:prepare-bundled-sections`,
 * `theme:loading:*`, `cart:change`) bzw. auf der Cart-Seite `location.reload()` — Cart-Drawer,
 * Cart-Count und Cart-Seite rerendern damit nativ.
 *
 * Nicht gekoppelt (bewusst): Menge 0 / Entfernen des Rings → die Gravurartikel-Zeile wird
 * verwaist (bestehende Orphan-Logik in clubmonee-engraving-cart-order.liquid ordnet sie ans
 * Listenende, bleibt entfernbar). Jede Ring/Gravur-Paarung ist über ihre eigene `_Gravur ID`
 * unabhängig — keine Kreuz-Kopplung bei mehreren gravierten Ringen.
 *
 * Schleifen-/Doppelrequest-Schutz: Ein `inFlight`-Flag serialisiert Updates; das nach dem
 * Update gefeuerte `cart:change` erzeugt kein neues DOM-`change`-Event, kann den Interceptor
 * also nicht erneut triggern.
 */

(() => {
  // Einmal-Init: Cart-Drawer (global) und Cart-Seite laden dieses Asset ggf. beide —
  // die Listener dürfen nur einmal existieren, sonst doppelte Requests.
  if (window.__clubmoneeEngravingCartSyncInit) {
    return;
  }

  window.__clubmoneeEngravingCartSyncInit = true;

  const routesRoot = window.Shopify?.routes?.root ?? '/';

  // ringKey -> articleKey (nur gepaarte Ringe mit vorhandener Gravurartikel-Zeile)
  let ringToArticle = new Map();
  let inFlight = false;

  const buildPairing = (cart) => {
    const map = new Map();

    if (!cart || !Array.isArray(cart.items)) {
      return map;
    }

    const articleKeyByGravurId = new Map();
    const rings = [];

    for (const item of cart.items) {
      const properties = item.properties ?? {};
      const gravurId = properties['_Gravur ID'];

      if (!gravurId) {
        continue;
      }

      if (properties['_Gravur Artikel']) {
        articleKeyByGravurId.set(gravurId, item.key);
      } else {
        rings.push({ key: item.key, gravurId });
      }
    }

    for (const ring of rings) {
      const articleKey = articleKeyByGravurId.get(ring.gravurId);

      if (articleKey) {
        map.set(ring.key, articleKey);
      }
    }

    return map;
  };

  const refreshPairingFromServer = async () => {
    try {
      const cart = await (await fetch(`${routesRoot}cart.js`)).json();
      ringToArticle = buildPairing(cart);
    } catch {
      /* Warenkorb nicht ladbar — Kopplung greift beim nächsten cart:change */
    }
  };

  const isCartPage = () => window.themeVariables?.settings?.pageType === 'cart';

  const syncPairedQuantity = async (ringKey, articleKey, quantity) => {
    if (inFlight) {
      return;
    }

    inFlight = true;

    const sectionsToBundle = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    let cartContent = null;
    let ok = false;

    try {
      const response = await fetch(`${routesRoot}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: { [ringKey]: quantity, [articleKey]: quantity },
          sections: [...sectionsToBundle].join(','),
        }),
      });

      ok = response.ok;
      cartContent = await response.json();
    } catch {
      ok = false;
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      inFlight = false;
    }

    if (!ok) {
      // Fehlgeschlagen: Theme seinen Warenkorb frisch ziehen lassen (kein stiller Fehlstand)
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return;
    }

    ringToArticle = buildPairing(cartContent);

    if (isCartPage()) {
      // Cart-Seite rerendert das Theme über einen Full-Reload (analog changeLineItemQuantity)
      window.location.reload();
      return;
    }

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'line-item:change', cart: cartContent } })
    );
  };

  /** true, wenn das Event vollständig von uns übernommen wurde (Theme soll es nicht mehr sehen) */
  const takeOver = (event, ringKey, quantity) => {
    // Nur Mengen >= 1 koppeln. Menge 0 = Entfernen → Ring weg, Gravurartikel verwaist (Theme).
    if (!Number.isInteger(quantity) || quantity < 1) {
      return false;
    }

    const articleKey = ringToArticle.get(ringKey);

    if (!articleKey) {
      return false;
    }

    event.stopImmediatePropagation();
    event.preventDefault();
    syncPairedQuantity(ringKey, articleKey, quantity);
    return true;
  };

  const onQuantityInputChange = (event) => {
    const input = event.target instanceof Element ? event.target.closest('[data-line-key]') : null;

    if (!input) {
      return;
    }

    takeOver(event, input.getAttribute('data-line-key'), parseInt(input.value, 10));
  };

  const onCartChangeLinkClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest(`[href*="${routesRoot}cart/change"]`) : null;

    if (!link) {
      return;
    }

    let url;

    try {
      url = new URL(link.href, window.location.origin);
    } catch {
      return;
    }

    const ringKey = url.searchParams.get('id');

    if (!ringKey) {
      return;
    }

    takeOver(event, ringKey, parseInt(url.searchParams.get('quantity'), 10));
  };

  // Capture-Phase: läuft VOR dem Bubble-Delegate des Theme-`line-item`-Elements.
  document.addEventListener('change', onQuantityInputChange, { capture: true });
  document.addEventListener('click', onCartChangeLinkClick, { capture: true });

  // Paarungs-Map aktuell halten: jeder Cart-Wechsel liefert den frischen Cart mit.
  document.addEventListener('cart:change', (event) => {
    const cart = event.detail?.cart;

    if (cart && Array.isArray(cart.items)) {
      ringToArticle = buildPairing(cart);
    } else {
      refreshPairingFromServer();
    }
  });

  refreshPairingFromServer();
})();
